import { useState, useEffect } from 'react'
import { AuthCard } from './AuthCard'
import { AuthInput } from './AuthInput'
import { AuthButton } from './AuthButton'
import { Mail, Check, ArrowLeft, RotateCcw } from 'lucide-react'
import { AuthState } from '@/hooks/useCustomAuth'

interface EmailVerificationFormProps {
  onSuccess?: () => void
  authState: any
  handleConfirmSignUp: (email: string, code: string) => Promise<{ success: boolean; error?: any }>
  handleResendCode: (email: string) => Promise<{ success: boolean; error?: any }>
  changeState: (newState: AuthState, tempEmail?: string, fromLogin?: boolean) => void
}

export function EmailVerificationForm({
  onSuccess,
  authState,
  handleConfirmSignUp,
  handleResendCode,
  changeState
}: EmailVerificationFormProps) {
  const [code, setCode] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [isResending, setIsResending] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!authState.tempEmail) return

    try {
      const result = await handleConfirmSignUp(authState.tempEmail, code)

      if (result?.success) {
        onSuccess?.()
      }
    } catch (error) {
      console.error('Email verification error:', error)
    }
  }

  const handleResend = async () => {
    if (!authState.tempEmail || resendCooldown > 0) return

    setIsResending(true)

    try {
      const result = await handleResendCode(authState.tempEmail)

      if (result?.success) {
        setResendCooldown(60) // 60 seconds cooldown
      }
    } catch (error) {
      console.error('Resend code error:', error)
    } finally {
      setIsResending(false)
    }
  }

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const title = authState.fromLogin
    ? 'Confirme seu e-mail para entrar'
    : 'Verificar e-mail'

  const subtitle = authState.fromLogin
    ? `Sua conta precisa ser verificada. Enviamos um código para ${authState.tempEmail}`
    : `Enviamos um código de verificação para ${authState.tempEmail}`

  return (
    <AuthCard
      title={title}
      subtitle={subtitle}
      icon={<Mail className="h-8 w-8 text-brand-dark" />}
    >
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="email-verification-form">
        <AuthInput
          label="Código de verificação"
          type="text"
          value={code}
          onChange={setCode}
          placeholder="Digite o código de 6 dígitos"
          required
          error={authState.error?.code === 'CodeMismatchException' ? authState.error.message : undefined}
          data-testid="email-verification-code-input"
        />

        {authState.error && authState.error.code !== 'CodeMismatchException' && (
          <div
            className="bg-error/20 border border-error text-error px-4 py-3 rounded-monumenta text-sm"
            data-testid="email-verification-error-message"
          >
            {authState.error.message}
          </div>
        )}

        <AuthButton
          type="submit"
          variant="primary"
          loading={authState.isLoading}
          disabled={!code}
          className="w-full"
          icon={<Check className="h-5 w-5" />}
          data-testid="email-verification-submit-button"
        >
          Verificar código
        </AuthButton>

        <div className="space-y-3">
          <AuthButton
            variant="secondary"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isResending}
            loading={isResending}
            className="w-full"
            icon={<RotateCcw className="h-4 w-4" />}
            data-testid="email-verification-resend-button"
          >
            {resendCooldown > 0
              ? `Reenviar em ${resendCooldown}s`
              : 'Reenviar código'
            }
          </AuthButton>

          <AuthButton
            variant="ghost"
            onClick={() => changeState('signIn')}
            className="w-full"
            icon={<ArrowLeft className="h-4 w-4" />}
            data-testid="email-verification-back-button"
          >
            Voltar ao login
          </AuthButton>
        </div>
      </form>
    </AuthCard>
  )
}